import "@/App.css";
import { useState, useEffect, lazy as _reactLazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { CountsProvider } from "./context/CountsContext";
import { useAuth } from "./context/AuthContext";
import { LocationProvider } from "./context/LocationContext";
import { UnsavedChangesProvider } from "./context/UnsavedChangesContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { MainLayout } from "./components/layout/MainLayout";
import { FieldLayout } from "./components/layout/FieldLayout";
import HelpWidget from "./components/HelpWidget";
import { Toaster } from "./components/ui/sonner";

// Pages
import Login from "./pages/Login";
import PcwLogin from "./pages/PcwLogin";

// After a deploy, open tabs on the old build request hashed chunk files that
// no longer exist → ChunkLoadError / white screen. Wrap every lazy() route so
// a failed dynamic import reloads ONCE (fresh index.html → new chunk names)
// instead of crashing into the error boundary. sessionStorage guards loops;
// the key is shared with the global handler in index.js.
const lazy = (factory) =>
  _reactLazy(async () => {
    try {
      return await factory();
    } catch (err) {
      const msg = String(err?.message || err || "");
      const isChunk = /Loading chunk|ChunkLoadError|dynamically imported module|Importing a module script failed|Loading CSS chunk/i.test(msg);
      if (isChunk) {
        let shouldReload = true;
        try {
          const KEY = "avida:chunk-reload-at";
          const last = Number(sessionStorage.getItem(KEY) || 0);
          if (Date.now() - last > 10000) {
            sessionStorage.setItem(KEY, String(Date.now()));
          } else {
            shouldReload = false; // already reloaded very recently — don't loop
          }
        } catch { /* sessionStorage blocked — still do one reload */ }
        if (!shouldReload) throw err;
        window.location.reload();
        // Never resolve so React keeps the Suspense fallback up until reload.
        return await new Promise(() => {});
      }
      throw err;
    }
  });

const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Register = lazy(() => import("./pages/Register"));
const CommunicationHub = lazy(() => import("./pages/CommunicationHub"));
// UnifiedInbox retired — /comms now redirects to the Ring Center (/phone).
const UnassignedComms = lazy(() => import("./pages/UnassignedComms"));
const ProjectsPortfolio = lazy(() => import("./pages/projects/ProjectsPortfolio"));
const NewProject = lazy(() => import("./pages/projects/NewProject"));
const ProjectDetail = lazy(() => import("./pages/projects/ProjectDetail"));
const MyWork = lazy(() => import("./pages/projects/MyWork"));
const WorkHub = lazy(() => import("./pages/work/WorkHub"));
const PhonePage = lazy(() => import("./pages/Phone"));
const VideoPage = lazy(() => import("./pages/Video"));
// MessagesPage is ~110 KB and only used on /messages — lazy-load it so it
// doesn't sit in the critical bundle for every other route.
const MessagesPage = lazy(() => import("./pages/Messages"));
const OverlayCalibratorPage = lazy(() => import("./pages/admin/OverlayCalibrator"));
const FaxAutoRoute = lazy(() => import("./pages/admin/FaxAutoRoute"));
const AdminAuditLog = lazy(() => import("./pages/admin/AuditLog"));
const SystemHealth = lazy(() => import("./pages/admin/SystemHealth"));
const AdminSetupPage = lazy(() => import("./pages/admin/Setup"));
import { SetupWizardAutoOpener } from "./components/setup/SetupWizard";
const AviCorpusAdmin = lazy(() => import("./pages/admin/AviCorpusAdmin"));
const AviAuditLogs = lazy(() => import("./pages/admin/AviAuditLogs"));
const TimeTracking = lazy(() => import("./pages/TimeTracking"));
const PcwSelfServePage = lazy(() => import("./pages/PcwSelfServe"));
const PublicCarePlanSign = lazy(() => import("./pages/healthcare/PublicCarePlanSign"));
const LearningLanding = lazy(() => import("./learn/LearningLanding"));
const LearningCatalog = lazy(() => import("./learn/LearningCatalog"));
const LearningTrackDetail = lazy(() => import("./learn/LearningTrackDetail"));
const LearningMy = lazy(() => import("./learn/LearningMy"));
const LearningLesson = lazy(() => import("./learn/LearningLesson"));
const LearningCertificates = lazy(() => import("./learn/LearningCertificates").then(m => ({ default: m.LearningCertificates })));
const LearningVerify = lazy(() => import("./learn/LearningCertificates").then(m => ({ default: m.LearningVerify })));
const LearningInvite = lazy(() => import("./learn/LearningInvite"));
const LearningAdmin = lazy(() => import("./learn/LearningAdmin"));
const SMSPage = lazy(() => import("./pages/SMS"));
const ContactsPage = lazy(() => import("./pages/Contacts"));
const CallHistoryPage = lazy(() => import("./pages/CallHistory"));
const SettingsPage = lazy(() => import("./pages/Settings"));
const AdminPage = lazy(() => import("./pages/Admin"));
const AuditLogPage = lazy(() => import("./pages/AuditLogPage"));
const PermissionsPage = lazy(() => import("./pages/PermissionsPage"));
const UnifiedCommsPage = lazy(() => import("./pages/UnifiedComms"));
const CommsEntry = lazy(() => import("./pages/CommsEntry"));
const CallStatusBoard = lazy(() => import("./pages/CallStatusBoard"));
import LegacyOrRedirect from "./components/LegacyOrRedirect";
import SoftphonePanel from "./components/phone/SoftphonePanel";
import { useContactSlideover } from "./components/contacts/ContactDetailSlideover";

// Simple mount wrapper — centers the fixed-size panel on a plain surface so
// it can be smoke-tested from /softphone. Real placement is expected to
// embed <SoftphonePanel /> directly in the host dashboard layout.
function SoftphonePanelMount() {
  return (
    <div className="min-h-screen flex items-start justify-center pt-6 pb-12 px-4 bg-[var(--surface,#F7F8FB)]" data-testid="softphone-mount">
      <SoftphonePanel />
    </div>
  );
}

const VoicemailPage = lazy(() => import("./pages/Voicemail"));
const AgentDashboard = lazy(() => import("./pages/AgentDashboard"));
const AnalyticsPage = lazy(() => import("./pages/Analytics"));
const LiveCallsAnalytics = lazy(() => import("./pages/LiveCallsAnalytics"));
const CallbacksPage = lazy(() => import("./pages/Callbacks"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const CommunicationsLogPage = lazy(() => import("./pages/CommunicationsLogPage"));
const PaPacketsPage = lazy(() => import("./pages/healthcare/onboarding/PaPacketsPage"));
const SupervisoryVisitsDue = lazy(() => import("./pages/healthcare/clients/SupervisoryVisitsDue"));
const SvPlanner = lazy(() => import("./pages/healthcare/clients/SvPlanner"));
const GhostUserReport = lazy(() => import("./pages/healthcare/GhostUserReport"));
const VoiceLineDiagnostics = lazy(() => import("./pages/healthcare/VoiceLineDiagnostics"));
const EhrCompleteness = lazy(() => import("./pages/healthcare/clients/EhrCompleteness"));
const ClinicalActions = lazy(() => import("./pages/healthcare/clients/ClinicalActions"));
const ClientAgreements = lazy(() => import("./pages/healthcare/ClientAgreements"));
const Hospitalizations = lazy(() => import("./pages/healthcare/clients/Hospitalizations"));
const ClientSurveysTracker = lazy(() => import("./pages/healthcare/clients/ClientSurveys"));
const McoDirectory = lazy(() => import("./pages/healthcare/admin/McoDirectory"));
const Integrations = lazy(() => import("./pages/healthcare/admin/Integrations"));
const Icd10Codes = lazy(() => import("./pages/healthcare/admin/Icd10Codes"));
const IadlTaskReference = lazy(() => import("./pages/healthcare/IadlTaskReference"));
const TasksPage = lazy(() => import("./pages/Tasks"));
const DocumentsPage = lazy(() => import("./pages/Documents"));
const CalendarPage = lazy(() => import("./pages/Calendar"));
const NotepadPage = lazy(() => import("./pages/Notepad"));
const ToolsPage = lazy(() => import("./pages/Tools"));
const BgcStatuteChecker = lazy(() => import("./pages/tools/BgcStatuteChecker"));
const BgcRulesets = lazy(() => import("./pages/admin/BgcRulesets"));
const BgcCases = lazy(() => import("./pages/tools/BgcCases"));
const BgcCaseDetail = lazy(() => import("./pages/tools/BgcCaseDetail"));
const ScreeningTool = lazy(() => import("./pages/healthcare/ScreeningTool"));
const WordProcessor = lazy(() => import("./pages/tools/WordProcessor"));
const PriorAuthTool = lazy(() => import("./pages/healthcare/PriorAuthTool"));
const EvvExceptionQueue = lazy(() => import("./pages/healthcare/EvvExceptionQueue"));
const EvvWorkerInterview = lazy(() => import("./pages/healthcare/EvvWorkerInterview"));
const EvvOpsDashboard = lazy(() => import("./pages/healthcare/EvvOpsDashboard"));
const EvvMissedBoard = lazy(() => import("./pages/healthcare/EvvMissedBoard"));
const Broadcasts = lazy(() => import("./pages/healthcare/Broadcasts"));

const PublicForm = lazy(() => import("./pages/PublicForm"));
const HealthcareDashboard = lazy(() => import("./pages/healthcare/HealthcareDashboard"));
const HoursUtilization = lazy(() => import("./pages/healthcare/HoursUtilization"));
const PatientList = lazy(() => import("./pages/healthcare/PatientList"));
const PatientDetail = lazy(() => import("./pages/healthcare/PatientDetail"));
const Visits = lazy(() => import("./pages/healthcare/Visits"));
const FieldVisits = lazy(() => import("./pages/healthcare/FieldVisits"));
const FieldVisitWizard = lazy(() => import("./pages/healthcare/FieldVisitWizard"));
const FieldVisitWizardV2 = lazy(() => import("./pages/healthcare/FieldVisitWizardV2"));
const FieldVisitDetail = lazy(() => import("./pages/healthcare/FieldVisitDetail"));
const VisitDetail = lazy(() => import("./pages/healthcare/VisitDetail"));
const AddPatient = lazy(() => import("./pages/healthcare/AddPatient"));
const EditPatient = lazy(() => import("./pages/healthcare/EditPatient"));
const CaregiverList = lazy(() => import("./pages/healthcare/CaregiverList"));
const AddCaregiver = lazy(() => import("./pages/healthcare/AddEmployee"));
const CaregiverDetail = lazy(() => import("./pages/healthcare/CaregiverDetail"));
const Claims = lazy(() => import("./pages/healthcare/Claims"));
const ClaimHoldQueue = lazy(() => import("./pages/healthcare/ClaimHoldQueue"));
const IncompleteTaskReport = lazy(() => import("./pages/healthcare/IncompleteTaskReport"));
const ClaimDetail = lazy(() => import("./pages/healthcare/ClaimDetail"));
const SandataExport = lazy(() => import("./pages/healthcare/SandataExport"));
const SandataReconcile = lazy(() => import("./pages/healthcare/SandataReconcile"));
const SandataValidation = lazy(() => import("./pages/healthcare/SandataValidation"));
const SandataPayrollPreview = lazy(() => import("./pages/healthcare/SandataPayrollPreview"));
const SandataClaimsPreview = lazy(() => import("./pages/healthcare/SandataClaimsPreview"));
const SandataDenialWorklist = lazy(() => import("./pages/healthcare/SandataDenialWorklist"));
const SandataDenialDashboard = lazy(() => import("./pages/healthcare/SandataDenialDashboard"));
const SandataFeeSchedule = lazy(() => import("./pages/healthcare/SandataFeeSchedule"));
const PortalWorkers = lazy(() => import("./pages/healthcare/PortalWorkers"));
const SandataAggregator = lazy(() => import("./pages/healthcare/SandataAggregator"));
const SandataOverlapWorklist = lazy(() => import("./pages/healthcare/SandataOverlapWorklist"));
const EvvGoLiveReadiness = lazy(() => import("./pages/healthcare/EvvGoLiveReadiness"));
const ContactRegistry = lazy(() => import("./pages/admin/ContactRegistry"));
const PhiWatchdog = lazy(() => import("./pages/admin/PhiWatchdog"));
const QASweep = lazy(() => import("./pages/QASweep"));
const Analytics = lazy(() => import("./pages/healthcare/Analytics"));
const PCSTAssessment = lazy(() => import("./pages/healthcare/PCSTAssessment"));
const CMS485 = lazy(() => import("./pages/healthcare/CMS485"));
const Form03182 = lazy(() => import("./pages/healthcare/Form03182"));
const NonSkilledAssessment = lazy(() => import("./pages/healthcare/NonSkilledAssessment"));
const Form485 = lazy(() => import("./pages/healthcare/Form485"));
const Form487 = lazy(() => import("./pages/healthcare/Form487"));
const FormF11136 = lazy(() => import("./pages/healthcare/FormF11136"));
const AlertCenter = lazy(() => import("./pages/healthcare/AlertCenter"));
const TaskQueue = lazy(() => import("./pages/healthcare/TaskQueue"));
const RulesEngine = lazy(() => import("./pages/healthcare/RulesEngine"));
const DocumentCenter = lazy(() => import("./pages/healthcare/DocumentCenter"));
const Filing = lazy(() => import("./pages/healthcare/Filing"));
const RedactionsQueue = lazy(() => import("./pages/healthcare/RedactionsQueue"));
const EHRRecords = lazy(() => import("./pages/healthcare/EHRRecords"));
const DisclosureAccounting = lazy(() => import("./pages/healthcare/DisclosureAccounting"));
const RetentionAdmin = lazy(() => import("./pages/healthcare/RetentionAdmin"));
const CriticalAlertsAdmin = lazy(() => import("./pages/healthcare/CriticalAlertsAdmin"));
const AuditLog = lazy(() => import("./pages/healthcare/AuditLog"));
const CarePlans = lazy(() => import("./pages/healthcare/CarePlans"));
const CarePlanWizard = lazy(() => import("./pages/healthcare/CarePlanWizard"));
const RenewalTrackerPage = lazy(() => import("./components/healthcare/RenewalTracker"));
const ExecutiveDashboard = lazy(() => import("./pages/healthcare/ExecutiveDashboard"));
const PAAlertRecipients = lazy(() => import("./pages/healthcare/PAAlertRecipients"));
const ServiceCarePlans = lazy(() => import("./pages/healthcare/ServiceCarePlans"));
const ScheduleCalendar = lazy(() => import("./pages/healthcare/ScheduleCalendar"));
const ShiftCoverage = lazy(() => import("./pages/healthcare/ShiftCoverage"));
const EvvAlerts = lazy(() => import("./pages/healthcare/EvvAlerts"));
const RelationshipLog = lazy(() => import("./pages/healthcare/RelationshipLog"));
const CaregiverRoster = lazy(() => import("./pages/healthcare/CaregiverRoster"));
const SupervisoryVisits = lazy(() => import("./pages/healthcare/SupervisoryVisits"));
const SupervisoryVisitForm = lazy(() => import("./pages/healthcare/SupervisoryVisitForm"));
const SupervisoryWizard = lazy(() => import("./pages/healthcare/clinical/SupervisoryWizard"));
const RNPreVisitBriefing = lazy(() => import("./pages/healthcare/RNPreVisitBriefing"));
const RNPostVisitHandoff = lazy(() => import("./pages/healthcare/RNPostVisitHandoff"));
const TaskSignoffMatrix = lazy(() => import("./pages/healthcare/TaskSignoffMatrix"));
const PAResponseWizard = lazy(() => import("./pages/healthcare/clinical/PAResponseWizard"));
const SupervisoryVisitDetail = lazy(() => import("./pages/healthcare/SupervisoryVisitDetail"));
const PATracker = lazy(() => import("./pages/healthcare/PATracker"));
const IncidentReporting = lazy(() => import("./pages/healthcare/IncidentReporting"));
const PCWPortal = lazy(() => import("./pages/healthcare/PCWPortal"));
const PcwClockIn = lazy(() => import("./pages/pcw/PcwClockIn"));
const DevTools = lazy(() => import("./pages/healthcare/DevTools"));
const LiveMonitor = lazy(() => import("./pages/healthcare/LiveMonitor"));
const EligibilityVerification = lazy(() => import("./pages/healthcare/EligibilityVerification"));
// Retired: EVV-session payroll view (consolidated into /healthcare/reports/payroll-summary)
const PaymentPosting = lazy(() => import("./pages/healthcare/PaymentPosting"));
const ARAging = lazy(() => import("./pages/healthcare/ARAging"));
const PayrollReconciliation = lazy(() => import("./pages/healthcare/PayrollReconciliation"));
const PayrollComparison = lazy(() => import("./pages/healthcare/PayrollComparison"));
const PayRates = lazy(() => import("./pages/healthcare/PayRates"));
const PayPeriods = lazy(() => import("./pages/healthcare/PayPeriods"));
const Invoices = lazy(() => import("./pages/healthcare/Invoices"));
const MonthEndClose = lazy(() => import("./pages/healthcare/MonthEndClose"));
const ChaseQueue = lazy(() => import("./pages/healthcare/ChaseQueue"));
const AdjustmentRules = lazy(() => import("./pages/healthcare/AdjustmentRules"));
const DispositionQueue = lazy(() => import("./pages/healthcare/DispositionQueue"));
const ReconciliationReports = lazy(() => import("./pages/healthcare/ReconciliationReports"));
const AuthorizationsOrders = lazy(() => import("./pages/healthcare/AuthorizationsOrders"));
const ClinicalHub = lazy(() => import("./pages/healthcare/ClinicalHub"));
const BillingDashboard = lazy(() => import("./pages/healthcare/BillingDashboard"));
const DocumentsEHR = lazy(() => import("./pages/healthcare/DocumentsEHR"));
const AdminSetup = lazy(() => import("./pages/healthcare/AdminSetup"));
const LocationsManagement = lazy(() => import("./pages/healthcare/LocationsManagement"));
const LocationDashboard = lazy(() => import("./pages/healthcare/LocationDashboard"));
const UserRoles = lazy(() => import("./pages/healthcare/UserRoles"));
const EVVClearinghouseSettings = lazy(() => import("./pages/healthcare/EVVClearinghouseSettings"));
const ComplianceOverview = lazy(() => import("./pages/healthcare/ComplianceOverview"));
const CensusListing = lazy(() => import("./pages/healthcare/CensusListing"));
const ServiceCodes = lazy(() => import("./pages/healthcare/ServiceCodes"));
const PayerSetup = lazy(() => import("./pages/healthcare/PayerSetup"));
const GeneralSettings = lazy(() => import("./pages/healthcare/GeneralSettings"));
const StaffAssignment = lazy(() => import("./pages/healthcare/StaffAssignment"));
const DuplicateScan = lazy(() => import("./pages/healthcare/admin/DuplicateScan"));
const AuthEpisodeRecon = lazy(() => import("./pages/healthcare/admin/AuthEpisodeRecon"));
const ClientAssignment = lazy(() => import("./pages/healthcare/ClientAssignment"));
const CMS485Tracker = lazy(() => import("./pages/healthcare/CMS485Tracker"));
const SandataVisitExport = lazy(() => import("./pages/healthcare/SandataVisitExport"));
const AvailityHub = lazy(() => import("./pages/healthcare/AvailityHub"));
const HospitalizationTracking = lazy(() => import("./pages/healthcare/HospitalizationTracking"));
const CommunicationLog = lazy(() => import("./pages/healthcare/CommunicationLog"));
const UserGuide = lazy(() => import("./pages/healthcare/UserGuide"));
const TrainingDownloads = lazy(() => import("./pages/TrainingDownloads"));
const ClientOnboardingWizard = lazy(() => import("./pages/healthcare/ClientOnboardingWizard"));
const KnowledgeBase = lazy(() => import("./pages/KnowledgeBase"));
const TrainingLibrary = lazy(() => import("./pages/TrainingLibrary"));
const TrainingPlayer = lazy(() => import("./pages/TrainingPlayer"));
const AdminTraining = lazy(() => import("./pages/AdminTraining"));
const UserManual = lazy(() => import("./pages/UserManual"));
const AdminBackups = lazy(() => import("./pages/AdminBackups"));
const AdminAgencySettings = lazy(() => import("./pages/AdminAgencySettings"));
const AnnouncementsAdmin = lazy(() => import("./pages/admin/Announcements"));
const IntegrationHealth = lazy(() => import("./pages/admin/IntegrationHealth"));
const ComplianceRulesets = lazy(() => import("./pages/admin/ComplianceRulesets"));
const ComplianceRulesetDetail = lazy(() => import("./pages/admin/ComplianceRulesetDetail"));
const CarePlanDetail = lazy(() => import("./pages/healthcare/CarePlanDetail"));
const CarePlanBuildRedirect = lazy(() => import("./pages/healthcare/CarePlanDetail").then(m => ({ default: m.CarePlanBuildRedirect })));
const PrePcstAnalysis = lazy(() => import("./pages/healthcare/PrePcstAnalysis"));
const PrePcstAnalysisBuildRedirect = lazy(() => import("./pages/healthcare/PrePcstAnalysis").then(m => ({ default: m.PrePcstAnalysisBuildRedirect })));
const CompanyWizard = lazy(() => import("./pages/healthcare/CompanyWizard"));
const EmployeeBadge = lazy(() => import("./pages/healthcare/EmployeeBadge"));
const TimesheetList = lazy(() => import("./pages/healthcare/TimesheetList"));
const PCWTimesheets = lazy(() => import("./pages/healthcare/PCWTimesheets"));
const TimesheetCatchUp = lazy(() => import("./pages/healthcare/TimesheetCatchUp"));
const TimesheetDetail = lazy(() => import("./pages/healthcare/TimesheetDetail"));
const HospSafeguards = lazy(() => import("./pages/healthcare/HospSafeguards"));
const FormTemplates = lazy(() => import("./pages/healthcare/FormTemplates"));
const EntityManagement = lazy(() => import("./pages/healthcare/EntityManagement"));
const SupportiveCare = lazy(() => import("./pages/healthcare/SupportiveCare"));
const EVVWorkflow = lazy(() => import("./pages/healthcare/EVVWorkflow"));
const LiveInCaregivers = lazy(() => import("./pages/healthcare/LiveInCaregivers"));const GeofenceExceptions = lazy(() => import("./pages/healthcare/GeofenceExceptions"));
const AltEvvControl = lazy(() => import("./pages/healthcare/AltEvvControl"));
const AuthorizationManagement = lazy(() => import("./pages/healthcare/AuthorizationManagement"));
const ProviderDirectory = lazy(() => import("./pages/healthcare/ProviderDirectory"));
const ComplianceQA = lazy(() => import("./pages/healthcare/ComplianceQA"));
const ComplianceCompanion = lazy(() => import("./pages/healthcare/compliance"));
const BillingReadiness = lazy(() => import("./pages/healthcare/BillingReadiness"));
const ReportsHub = lazy(() => import("./pages/healthcare/ReportsHub"));
const RevenueCycleReport = lazy(() => import("./pages/healthcare/RevenueCycleReport"));
const UsageReport = lazy(() => import("./pages/healthcare/UsageReport"));
const DenialAnalyticsReport = lazy(() => import("./pages/healthcare/DenialAnalyticsReport"));
const TimelyFilingReport = lazy(() => import("./pages/healthcare/TimelyFilingReport"));
const EvvUtilizationReport = lazy(() => import("./pages/healthcare/EvvUtilizationReport"));
const PayrollSummaryReport = lazy(() => import("./pages/healthcare/PayrollSummaryReport"));
const CaregiverHoursReport = lazy(() => import("./pages/healthcare/CaregiverHoursReport"));
const ShiftCoverageReport = lazy(() => import("./pages/healthcare/ShiftCoverageReport"));
const OvertimeWatchReport = lazy(() => import("./pages/healthcare/OvertimeWatchReport"));
const MissedVisitLogReport = lazy(() => import("./pages/healthcare/MissedVisitLogReport"));
const AuthUtilizationReport = lazy(() => import("./pages/healthcare/AuthUtilizationReport"));
const SurveyAuditPackReport = lazy(() => import("./pages/healthcare/SurveyAuditPackReport"));
const SVComplianceReport = lazy(() => import("./pages/healthcare/SVComplianceReport"));
const CensusReport = lazy(() => import("./pages/healthcare/CensusReport"));
const LostReasonsReport = lazy(() => import("./pages/healthcare/LostReasonsReport"));
const TerminationsReport = lazy(() => import("./pages/healthcare/TerminationsReport"));
const SandataGoLive = lazy(() => import("./pages/healthcare/SandataGoLive"));
const UsageLimits = lazy(() => import("./pages/healthcare/UsageLimits"));
const StaffEligibility = lazy(() => import("./pages/healthcare/StaffEligibility"));
const AINarratives = lazy(() => import("./pages/healthcare/AINarratives"));
// Retired: EVV-session payroll dashboard (consolidated into /healthcare/reports/payroll-summary)
const ClientPortal = lazy(() => import("./pages/healthcare/ClientPortal"));
const PhysicianPortal = lazy(() => import("./pages/healthcare/PhysicianPortal"));
const SecuritySettings = lazy(() => import("./pages/healthcare/SecuritySettings"));
const AiGovernance = lazy(() => import("./pages/healthcare/admin/AiGovernance"));
const Lifecycles = lazy(() => import("./pages/healthcare/Lifecycles"));
const OnboardingWizard = lazy(() => import("./pages/healthcare/OnboardingWizard"));
const GoogleCalendar = lazy(() => import("./pages/healthcare/GoogleCalendar"));
const GoogleDrive = lazy(() => import("./pages/healthcare/GoogleDrive"));
const NotificationEngine = lazy(() => import("./pages/healthcare/NotificationEngine"));
const DenialManagement = lazy(() => import("./pages/healthcare/DenialManagement"));
const BatchOperations = lazy(() => import("./pages/healthcare/BatchOperations"));
const DashboardCustomizer = lazy(() => import("./pages/healthcare/DashboardCustomizer"));
const ActivityFeed = lazy(() => import("./pages/healthcare/ActivityFeed"));
const ClientSurveys = lazy(() => import("./pages/healthcare/ClientSurveys"));
const ESignatures = lazy(() => import("./pages/healthcare/ESignatures"));
const PARenewal = lazy(() => import("./pages/healthcare/PARenewal"));
const PredictiveAnalytics = lazy(() => import("./pages/healthcare/PredictiveAnalytics"));
const AuditReadiness = lazy(() => import("./pages/healthcare/AuditReadiness"));
const ClinicalCompliance = lazy(() => import("./pages/healthcare/ClinicalCompliance"));
const Grievances = lazy(() => import("./pages/healthcare/Grievances"));
const RnAssignments = lazy(() => import("./pages/healthcare/RnAssignments"));
const NurseCalendar = lazy(() => import("./pages/healthcare/NurseCalendar"));
const SurveyorPacket = lazy(() => import("./pages/healthcare/SurveyorPacket"));
const DailyDigest = lazy(() => import("./pages/healthcare/DailyDigest"));
const TravelLog = lazy(() => import("./pages/healthcare/TravelLog"));
const StaffCredentialDashboard = lazy(() => import("./pages/healthcare/StaffCredentialDashboard"));
const PASubmissionWorkbench = lazy(() => import("./pages/healthcare/PASubmissionWorkbench"));
const UnmatchedFaxes = lazy(() => import("./pages/healthcare/UnmatchedFaxes"));
const RoutineItinerary = lazy(() => import("./pages/healthcare/RoutineItinerary"));
const MCOTravelLog = lazy(() => import("./pages/healthcare/MCOTravelLog"));
const ItineraryCoverage = lazy(() => import("./pages/healthcare/ItineraryCoverage"));
const ClaimValidator = lazy(() => import("./pages/healthcare/ClaimValidator"));
import Forbidden403 from "./pages/Forbidden403";
import RingCenterPreview from "./pages/RingCenterPreview";
import RingCenter from "./pages/RingCenter";
import InterpreterRequests from "./pages/InterpreterRequests";
import ErrorPage from "./pages/ErrorPage";
import { WalkthroughProvider, WalkthroughHelpButton } from "./components/walkthrough/WalkthroughProvider";
import { ConfirmProvider } from "./components/ConfirmDialog";
import FloatingChats from "./components/FloatingChats";

import { isAppDomain } from "./lib/domainMode";

// Route landing: simple-phone employees go straight to their My Line dialer.
function HomeRedirect({ fallback }) {
  const { user } = useAuth();
  return <Navigate to={user?.simple_phone ? "/phone" : fallback} replace />;
}

// `/healthcare/staff` is a common alias for the caregiver roster. The canonical
// pages live under `/healthcare/caregivers`, so `staff/:id` forwards to the
// matching caregiver detail (bare `/healthcare/staff` uses a plain <Navigate>).
function CaregiverDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/healthcare/caregivers/${id}`} replace />;
}

// The legacy 9-step PCST Initial Visit Wizard was retired. Any lingering link
// or bookmark to /pcst-wizard now lands on the canonical PCST Assessment.
function PcstWizardRedirect() {
  const { id } = useParams();
  return <Navigate to={`/healthcare/patients/${id}/pcst`} replace />;
}


// `/contacts/:id` has no full page — the canonical contact detail is the
// right-side slideover mounted in MainLayout. This route opens that
// slideover for the requested contact and drops the user onto the
// contacts surface underneath, so direct links / bookmarks / post-save
// redirects resolve instead of hitting the 404 catch-all.
function ContactDetailRedirect() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { open } = useContactSlideover();
  useEffect(() => {
    if (id) open({ contactId: id });
    navigate("/phone?tab=contacts", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  return null;
}

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="route-loading">
      <div className="loading-spinner" />
    </div>
  );
}

function App() {
  const defaultRoute = "/healthcare";
  return (
    <AuthProvider>
      <CountsProvider>
      <LocationProvider>
      <UnsavedChangesProvider>
      <BrowserRouter>
      <ErrorBoundary>
      <WalkthroughProvider>
        <ConfirmProvider>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/pcw-login" element={<PcwLogin />} />
          <Route path="/pcw/onboard/:token" element={<PcwSelfServePage />} />
          <Route path="/sign/care-plan/:token" element={<PublicCarePlanSign />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/register" element={<Register />} />
          <Route path="/form/:token" element={<PublicForm />} />
          <Route path="/403" element={<Forbidden403 />} />
          <Route path="/error/:code" element={<ErrorPage />} />

          {/* Avida Learning (public) — learn.avidahealth.org */}
          <Route path="/learn" element={<LearningLanding />} />
          <Route path="/learn/catalog" element={<LearningCatalog />} />
          <Route path="/learn/course/:trackId" element={<LearningTrackDetail />} />
          <Route path="/learn/my" element={<LearningMy />} />
          <Route path="/learn/lesson/:lessonId" element={<LearningLesson />} />
          <Route path="/learn/certificates" element={<LearningCertificates />} />
          <Route path="/learn/verify/:certId" element={<LearningVerify />} />
          <Route path="/learn/invite/:token" element={<LearningInvite />} />
          <Route path="/learn/admin" element={<LearningAdmin />} />
          <Route path="/ring-center-preview" element={<RingCenterPreview />} />
          
          {/* Protected Routes */}
          {/* Caregiver (PCW) shell — clean field routes, no office chrome (Staff/PCW isolation, Phase 3) */}
          <Route element={<FieldLayout />}>
            <Route path="/pcw" element={<PCWPortal />} />
            <Route path="/pcw/clock-in" element={<PcwClockIn />} />
            <Route path="/healthcare/pcw-portal" element={<PCWPortal />} />
          </Route>

          <Route element={<MainLayout />}>
            <Route path="/dashboard" element={<Navigate to="/healthcare" replace />} />
            {/* Softphone panel — standalone mount for testing. Also embeddable
                anywhere as <SoftphonePanel /> (500×1000 fixed). */}
            <Route path="/softphone" element={<SoftphonePanelMount />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/phone" element={<CommsEntry />} />
            <Route path="/call-status" element={<CallStatusBoard />} />
            <Route path="/interpreter-requests" element={<InterpreterRequests />} />
            <Route path="/ring-center" element={<RingCenter />} />
            <Route path="/video" element={<VideoPage />} />
            <Route path="/messages" element={
              <Suspense fallback={
                <div className="flex items-center justify-center h-screen text-slate-400 text-sm">
                  Loading Team Chat…
                </div>
              }>
                <MessagesPage />
              </Suspense>
            } />
            <Route path="/sms" element={<LegacyOrRedirect target="/phone?tab=texts" legacyElement={<SMSPage />} />} />
            <Route path="/comms" element={<Navigate to="/phone" replace />} />
            <Route path="/comms/unassigned" element={<UnassignedComms />} />
            <Route path="/work" element={<WorkHub />} />
            <Route path="/projects" element={<ProjectsPortfolio />} />
            <Route path="/projects/new" element={<NewProject />} />
            <Route path="/projects/:id" element={<ProjectDetail />} />
            <Route path="/my-work" element={<MyWork />} />
            <Route path="/chat" element={<ChatPage />} />
            <Route path="/voicemail" element={<LegacyOrRedirect target="/phone?tab=voicemail" legacyElement={<VoicemailPage />} />} />
            <Route path="/contacts" element={<LegacyOrRedirect target="/phone?tab=contacts" legacyElement={<ContactsPage />} />} />
            <Route path="/contacts/:id" element={<ContactDetailRedirect />} />
            <Route path="/history" element={<CallHistoryPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/calibrate/:docCode" element={<OverlayCalibratorPage />} />
            <Route path="/admin/fax-auto-route" element={<FaxAutoRoute />} />
            <Route path="/admin/audit-log" element={<AdminAuditLog />} />
            <Route path="/admin/health" element={<SystemHealth />} />
            <Route path="/admin/setup" element={<AdminSetupPage />} />
            <Route path="/audit-log" element={<AuditLogPage />} />
            <Route path="/permissions" element={<PermissionsPage />} />
            <Route path="/agents" element={<AgentDashboard />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/analytics/live-calls" element={<LiveCallsAnalytics />} />
            <Route path="/callbacks" element={<CallbacksPage />} />
            <Route path="/fax" element={<Navigate to="/phone?tab=fax" replace />} />
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/documents" element={<DocumentsPage />} />
            <Route path="/notepad" element={<NotepadPage />} />
            <Route path="/tools" element={<ToolsPage />} />
            <Route path="/tools/screening" element={<ScreeningTool />} />
            <Route path="/tools/word-processor" element={<WordProcessor />} />
            <Route path="/tools/prior-authorization" element={<PriorAuthTool />} />
            <Route path="/tools/evv-exceptions" element={<EvvExceptionQueue />} />
            <Route path="/tools/evv-interview" element={<EvvWorkerInterview />} />
            <Route path="/tools/evv-dashboard" element={<EvvOpsDashboard />} />
            <Route path="/tools/evv-ops" element={<EvvOpsDashboard />} />
            <Route path="/tools/evv-missed" element={<EvvMissedBoard />} />
            <Route path="/healthcare/broadcasts" element={<LegacyOrRedirect target="/phone?tab=text-blast" legacyElement={<Broadcasts />} />} />
            <Route path="/knowledge-base" element={<KnowledgeBase />} />
            <Route path="/knowledge-base/user-manual" element={<UserManual />} />
            {/* Alias so the guide is reachable from anywhere via a friendly URL */}
            <Route path="/help" element={<UserGuide />} />
            <Route path="/knowledge-base" element={<UserGuide />} />
            <Route path="/admin/backups" element={<AdminBackups />} />
            <Route path="/admin/agency-settings" element={<AdminAgencySettings />} />
            <Route path="/admin/announcements" element={<AnnouncementsAdmin />} />
            <Route path="/admin/integration-health" element={<IntegrationHealth />} />
            <Route path="/admin/compliance-rulesets" element={<ComplianceRulesets />} />
            <Route path="/admin/compliance-rulesets/:id" element={<ComplianceRulesetDetail />} />
            <Route path="/healthcare/care-plans/build" element={<CarePlanBuildRedirect />} />
            <Route path="/healthcare/care-plans/:planId" element={<CarePlanDetail />} />
            <Route path="/healthcare/pre-pcst-analyses/build" element={<PrePcstAnalysisBuildRedirect />} />
            <Route path="/healthcare/pre-pcst-analyses/:aid" element={<PrePcstAnalysis />} />
            <Route path="/training" element={<TrainingLibrary />} />
            <Route path="/training/:slug" element={<TrainingPlayer />} />
            <Route path="/admin/training" element={<AdminTraining />} />
            <Route path="/healthcare" element={<HealthcareDashboard />} />
            <Route path="/healthcare/hours-utilization" element={<HoursUtilization />} />
            <Route path="/healthcare/patients" element={<PatientList />} />
            <Route path="/healthcare/patients/new" element={<AddPatient />} />
            <Route path="/healthcare/patients/:id" element={<PatientDetail />} />
            <Route path="/healthcare/patients/:id/edit" element={<EditPatient />} />
            <Route path="/healthcare/patients/:id/pcst" element={<PCSTAssessment />} />
            <Route path="/healthcare/patients/:id/pcst-wizard" element={<PcstWizardRedirect />} />
            <Route path="/healthcare/patients/:id/cms485" element={<CMS485 />} />
            <Route path="/healthcare/patients/:id/f03182" element={<Form03182 />} />
            <Route path="/healthcare/patients/:patientId/non-skilled-assessment" element={<NonSkilledAssessment />} />
            <Route path="/healthcare/patients/:id/485-poc" element={<Form485 />} />
            <Route path="/healthcare/patients/:id/487" element={<Form487 />} />
            <Route path="/healthcare/patients/:id/f11136" element={<FormF11136 />} />
            <Route path="/healthcare/patients/:id/pa-submission" element={<PASubmissionWorkbench />} />
            <Route path="/healthcare/fax/unmatched" element={<UnmatchedFaxes />} />
            <Route path="/healthcare/patients/:id/onboarding" element={<ClientOnboardingWizard />} />
            <Route path="/healthcare/patients/:patientId/hospitalizations" element={<HospitalizationTracking />} />
            <Route path="/healthcare/patients/:patientId/communications" element={<CommunicationLog />} />
            <Route path="/healthcare/caregivers" element={<CaregiverList />} />
            <Route path="/healthcare/staff" element={<Navigate to="/healthcare/caregivers" replace />} />
            <Route path="/healthcare/staff/:id" element={<CaregiverDetailRedirect />} />
            <Route path="/healthcare/caregivers/new" element={<AddCaregiver />} />
            <Route path="/healthcare/caregivers/:id" element={<CaregiverDetail />} />
            <Route path="/communications-log" element={<LegacyOrRedirect target="/phone?tab=calls" legacyElement={<CommunicationsLogPage />} />} />
            <Route path="/healthcare/caregivers/:id/itinerary" element={<RoutineItinerary />} />
            <Route path="/healthcare/mco-travel-log" element={<MCOTravelLog />} />
            <Route path="/healthcare/mco-travel-log/coverage" element={<ItineraryCoverage />} />
            <Route path="/healthcare/roster" element={<CaregiverRoster />} />
            <Route path="/healthcare/rn-assignments" element={<RnAssignments />} />
            <Route path="/healthcare/my-calendar" element={<NurseCalendar />} />
            <Route path="/healthcare/supervisory-visits" element={<SupervisoryVisits />} />
            <Route path="/healthcare/supervisory-visits/new" element={<SupervisoryVisitForm />} />
            <Route path="/healthcare/patients/:id/supervisory-wizard" element={<SupervisoryWizard />} />
            <Route path="/healthcare/patients/:id/task-signoffs" element={<TaskSignoffMatrix />} />
            <Route path="/healthcare/patients/:id/pa-response-wizard" element={<PAResponseWizard />} />
            <Route path="/healthcare/patients/:id/pre-visit-brief" element={<RNPreVisitBriefing />} />
            <Route path="/healthcare/patients/:id/post-visit-handoff" element={<RNPostVisitHandoff />} />
            <Route path="/healthcare/supervisory-visits/:visitId" element={<SupervisoryVisitDetail />} />
            <Route path="/healthcare/pa-tracker" element={<PATracker />} />
            <Route path="/healthcare/fax" element={<Navigate to="/phone?tab=fax" replace />} />
            <Route path="/healthcare/incidents" element={<IncidentReporting />} />
            <Route path="/healthcare/alerts" element={<AlertCenter />} />
            <Route path="/healthcare/tasks" element={<TaskQueue />} />
            <Route path="/healthcare/rules" element={<RulesEngine />} />
            <Route path="/healthcare/documents" element={<EHRRecords />} />
            <Route path="/healthcare/forms" element={<Filing />} />
            <Route path="/healthcare/forms-legacy" element={<DocumentCenter />} />
            <Route path="/healthcare/portal" element={<ClientPortal />} />
            <Route path="/healthcare/physician-portal" element={<PhysicianPortal />} />
            <Route path="/healthcare/dev-tools" element={<DevTools />} />
            <Route path="/healthcare/live-monitor" element={<LiveMonitor />} />
            <Route path="/healthcare/eligibility" element={<EligibilityVerification />} />
            <Route path="/healthcare/payroll" element={<Navigate to="/healthcare/reports/payroll-summary" replace />} />
            <Route path="/healthcare/auth" element={<AuthorizationsOrders />} />
            <Route path="/healthcare/auth/485-tracker" element={<CMS485Tracker />} />
            <Route path="/healthcare/auth/physician-orders" element={<AuthorizationsOrders />} />
            <Route path="/healthcare/auth/medical-records" element={<AuthorizationsOrders />} />
            <Route path="/healthcare/clinical" element={<ClinicalHub />} />
            <Route path="/healthcare/clinical/pcst" element={<ClinicalHub />} />
            <Route path="/healthcare/clinical/cms485" element={<ClinicalHub />} />
            {/* iter 508 · removed `/healthcare/clinical/non-skilled` — NSA
                is client-scoped only; the sole entry is
                `/healthcare/patients/:patientId/non-skilled-assessment`
                mounted below. Anyone hitting the old URL bounces via
                the SPA 404 to the healthcare dashboard. */}
            <Route path="/healthcare/billing" element={<BillingDashboard />} />
            <Route path="/healthcare/payments" element={<PaymentPosting />} />
            <Route path="/healthcare/ar-aging" element={<ARAging />} />
            <Route path="/healthcare/payroll-recon" element={<PayrollReconciliation />} />
            <Route path="/healthcare/payroll-comparison" element={<PayrollComparison />} />
            <Route path="/healthcare/pay-rates" element={<PayRates />} />
            <Route path="/healthcare/pay-periods" element={<PayPeriods />} />
            <Route path="/healthcare/invoices" element={<Invoices />} />
            <Route path="/healthcare/month-end" element={<MonthEndClose />} />
            <Route path="/healthcare/chase-queue" element={<ChaseQueue />} />
            <Route path="/healthcare/adjustment-rules" element={<AdjustmentRules />} />
            <Route path="/healthcare/disposition-queue" element={<DispositionQueue />} />
            <Route path="/healthcare/reconciliation-reports" element={<ReconciliationReports />} />
            <Route path="/healthcare/audit-log" element={<AuditLog />} />
            <Route path="/healthcare/care-plans" element={<CarePlans />} />
            <Route path="/healthcare/care-plan-wizard" element={<CarePlanWizard />} />
            <Route path="/healthcare/renewal-tracker/:authId" element={<RenewalTrackerPage />} />
            <Route path="/healthcare/executive" element={<ExecutiveDashboard />} />
            <Route path="/healthcare/settings/pa-alert-recipients" element={<PAAlertRecipients />} />
            <Route path="/healthcare/service-care-plans" element={<ServiceCarePlans />} />
            {/* Aliases — commonly-mistyped shortcuts that used to 404 */}
            <Route path="/healthcare/service-plans" element={<ServiceCarePlans />} />
            <Route path="/healthcare/clients" element={<PatientList />} />
            <Route path="/healthcare/clients/new" element={<AddPatient />} />
            <Route path="/healthcare/clients/:id" element={<PatientDetail />} />
            <Route path="/healthcare/schedule" element={<ScheduleCalendar />} />
            <Route path="/healthcare/scheduling/coverage" element={<ShiftCoverage />} />
            <Route path="/healthcare/evv-alerts" element={<EvvAlerts />} />
            <Route path="/healthcare/relationship-log" element={<RelationshipLog />} />
            <Route path="/healthcare/admin" element={<AdminSetup />} />
            <Route path="/healthcare/admin/entities" element={<EntityManagement />} />
            <Route path="/healthcare/admin/locations" element={<LocationsManagement />} />
            <Route path="/healthcare/admin/redactions" element={<RedactionsQueue />} />
            <Route path="/healthcare/admin/disclosures" element={<DisclosureAccounting />} />
            <Route path="/healthcare/admin/retention" element={<RetentionAdmin />} />
            <Route path="/healthcare/admin/critical-alerts" element={<CriticalAlertsAdmin />} />
            <Route path="/healthcare/admin/location-dashboard" element={<LocationDashboard />} />
            <Route path="/healthcare/admin/user-roles" element={<UserRoles />} />
            <Route path="/healthcare/admin/evv-clearinghouse" element={<EVVClearinghouseSettings />} />
            <Route path="/healthcare/admin/compliance" element={<ComplianceOverview />} />
            <Route path="/healthcare/admin/ai-governance" element={<AiGovernance />} />
            <Route path="/healthcare/lifecycles" element={<Lifecycles />} />
            <Route path="/healthcare/census" element={<CensusListing />} />
            <Route path="/healthcare/admin/service-codes" element={<ServiceCodes />} />
            <Route path="/healthcare/admin/payers" element={<PayerSetup />} />
            <Route path="/healthcare/admin/settings" element={<GeneralSettings />} />
            <Route path="/healthcare/admin/staff-assignment" element={<StaffAssignment />} />
            <Route path="/healthcare/admin/duplicate-scan" element={<DuplicateScan />} />
            <Route path="/healthcare/admin/auth-recon" element={<AuthEpisodeRecon />} />
            <Route path="/healthcare/admin/client-assignment" element={<ClientAssignment />} />
            <Route path="/healthcare/visits" element={<Visits />} />
            <Route path="/healthcare/field-visits" element={<FieldVisits />} />
            <Route path="/healthcare/field-visits/new" element={<FieldVisitWizard />} />
            <Route path="/healthcare/field-visits/:id/wizard" element={<FieldVisitWizardV2 />} />
            <Route path="/healthcare/field-visits/:id" element={<FieldVisitDetail />} />
            <Route path="/healthcare/visits/:visitId" element={<VisitDetail />} />
            <Route path="/healthcare/claims" element={<Claims />} />
            <Route path="/healthcare/claim-hold-queue" element={<ClaimHoldQueue />} />
            <Route path="/healthcare/incomplete-task-report" element={<IncompleteTaskReport />} />
            <Route path="/healthcare/claims/:claimId" element={<ClaimDetail />} />
            <Route path="/healthcare/sandata" element={<SandataExport />} />
            <Route path="/healthcare/sandata-export" element={<SandataVisitExport />} />
            <Route path="/healthcare/sandata-reconcile" element={<SandataReconcile />} />
            <Route path="/healthcare/sandata-validation" element={<SandataValidation />} />
            <Route path="/healthcare/sandata-payroll-preview" element={<SandataPayrollPreview />} />
            <Route path="/healthcare/sandata-claims-preview" element={<SandataClaimsPreview />} />
            <Route path="/healthcare/sandata-denials" element={<SandataDenialWorklist />} />
            <Route path="/healthcare/sandata-denials-dashboard" element={<SandataDenialDashboard />} />
            <Route path="/healthcare/sandata-fee-schedule" element={<SandataFeeSchedule />} />
            <Route path="/healthcare/sandata-overlap" element={<SandataOverlapWorklist />} />
            <Route path="/healthcare/evv-portal-workers" element={<PortalWorkers />} />
            <Route path="/healthcare/sandata-aggregator" element={<SandataAggregator />} />
            <Route path="/tools/sandata-aggregator" element={<SandataAggregator />} />
            <Route path="/healthcare/evv-readiness" element={<EvvGoLiveReadiness />} />
            <Route path="/tools/evv-readiness" element={<EvvGoLiveReadiness />} />
            <Route path="/admin/contact-registry" element={<ContactRegistry />} />
            <Route path="/tools/contact-registry" element={<ContactRegistry />} />
            <Route path="/admin/phi-watchdog" element={<PhiWatchdog />} />
            <Route path="/tools/phi-watchdog" element={<PhiWatchdog />} />
            <Route path="/admin/qa-sweep" element={<QASweep />} />
            <Route path="/tools/qa-sweep" element={<QASweep />} />
            <Route path="/admin/avi/corpus" element={<AviCorpusAdmin />} />
            <Route path="/admin/avi/audit-log" element={<AviAuditLogs />} />
            {/* DHS 12 Statute Checker — Phase 1 of BGC build spec (iter 479) */}
            <Route path="/tools/bgc-statute-check" element={<BgcStatuteChecker />} />
            <Route path="/admin/bgc-rulesets" element={<BgcRulesets />} />
            {/* BGC Phase 2 — case upload + Claude extraction + verbatim-quote
                gate + admin sign-off (iter 480) */}
            <Route path="/tools/bgc-cases" element={<BgcCases />} />
            <Route path="/tools/bgc-cases/:caseId" element={<BgcCaseDetail />} />
            <Route path="/time-tracking" element={<TimeTracking />} />
            <Route path="/healthcare/availity" element={<AvailityHub />} />
            <Route path="/healthcare/analytics" element={<Analytics />} />
            <Route path="/healthcare/guide" element={<UserGuide />} />
            <Route path="/training/downloads" element={<TrainingDownloads />} />
            <Route path="/admin/training-downloads" element={<TrainingDownloads />} />
            <Route path="/tools/training-downloads" element={<TrainingDownloads />} />
            <Route path="/healthcare/company/new" element={<CompanyWizard />} />
            <Route path="/healthcare/employee-badge" element={<EmployeeBadge />} />
            <Route path="/healthcare/timesheets" element={<TimesheetList />} />
            <Route path="/healthcare/timesheets/:timesheetId" element={<TimesheetDetail />} />
            <Route path="/healthcare/pcw-timesheets" element={<PCWTimesheets />} />
            <Route path="/healthcare/pcw-timesheets/catch-up" element={<TimesheetCatchUp />} />
            <Route path="/healthcare/hosp-safeguards" element={<HospSafeguards />} />
            <Route path="/healthcare/security-settings" element={<SecuritySettings />} />
            <Route path="/admin/security-settings" element={<SecuritySettings />} />
            <Route path="/healthcare/onboarding/pa-packets" element={<PaPacketsPage />} />
            <Route path="/healthcare/clients/supervisory-visits-due" element={<SupervisoryVisitsDue />} />
            <Route path="/healthcare/sv-planner" element={<SvPlanner />} />
            <Route path="/healthcare/ghost-user-report" element={<GhostUserReport />} />
            <Route path="/healthcare/voice-line-diagnostics" element={<VoiceLineDiagnostics />} />
            <Route path="/healthcare/clients/ehr-completeness" element={<EhrCompleteness />} />
            <Route path="/healthcare/clients/clinical" element={<ClinicalActions />} />
            <Route path="/healthcare/clients/agreements" element={<ClientAgreements />} />
            <Route path="/healthcare/clients/hospitalizations" element={<Hospitalizations />} />
            <Route path="/healthcare/clients/surveys" element={<ClientSurveysTracker />} />
            <Route path="/healthcare/form-templates" element={<FormTemplates />} />
            <Route path="/healthcare/supportive-care" element={<SupportiveCare />} />
            <Route path="/healthcare/evv-workflow" element={<EVVWorkflow />} />
            <Route path="/healthcare/live-in" element={<LiveInCaregivers />} />
            <Route path="/healthcare/evv/go-live" element={<AltEvvControl />} />
            <Route path="/healthcare/evv/geofence-exceptions" element={<GeofenceExceptions />} />
            <Route path="/healthcare/authorization-management" element={<AuthorizationManagement />} />
            <Route path="/healthcare/providers" element={<ProviderDirectory />} />
            <Route path="/healthcare/compliance-qa" element={<ComplianceQA />} />
            <Route path="/healthcare/compliance-companion/:tab?" element={<ComplianceCompanion />} />
            <Route path="/healthcare/billing-readiness" element={<BillingReadiness />} />
            <Route path="/healthcare/reports" element={<ReportsHub />} />
            <Route path="/healthcare/reports/revenue-cycle" element={<RevenueCycleReport />} />
            <Route path="/healthcare/reports/usage" element={<UsageReport />} />
            <Route path="/healthcare/reports/denials" element={<DenialAnalyticsReport />} />
            <Route path="/healthcare/reports/timely-filing" element={<TimelyFilingReport />} />
            <Route path="/healthcare/reports/evv-utilization" element={<EvvUtilizationReport />} />
            <Route path="/healthcare/reports/payroll-summary" element={<PayrollSummaryReport />} />
            <Route path="/healthcare/reports/caregiver-hours" element={<CaregiverHoursReport />} />
            <Route path="/healthcare/reports/shift-coverage" element={<ShiftCoverageReport />} />
            <Route path="/healthcare/reports/overtime-watch" element={<OvertimeWatchReport />} />
            <Route path="/healthcare/reports/missed-visits" element={<MissedVisitLogReport />} />
            <Route path="/healthcare/reports/auth-utilization" element={<AuthUtilizationReport />} />
            <Route path="/healthcare/reports/audit-pack" element={<SurveyAuditPackReport />} />
            <Route path="/healthcare/reports/sv-compliance" element={<SVComplianceReport />} />
            <Route path="/healthcare/reports/census" element={<CensusReport />} />
            <Route path="/healthcare/reports/lost-reasons" element={<LostReasonsReport />} />
            <Route path="/healthcare/reports/terminations" element={<TerminationsReport />} />
            <Route path="/healthcare/sandata-golive" element={<SandataGoLive />} />
            <Route path="/healthcare/usage-limits" element={<UsageLimits />} />
            <Route path="/healthcare/claim-validator" element={<ClaimValidator />} />
            <Route path="/healthcare/staff-eligibility" element={<StaffEligibility />} />
            <Route path="/healthcare/ai-narratives" element={<AINarratives />} />
            <Route path="/healthcare/payroll-dashboard" element={<Navigate to="/healthcare/reports/payroll-summary" replace />} />
            <Route path="/healthcare/security" element={<SecuritySettings />} />
            <Route path="/healthcare/admin/initial-setup" element={<OnboardingWizard />} />
            <Route path="/healthcare/admin/mcos" element={<McoDirectory />} />
            <Route path="/healthcare/admin/integrations" element={<Integrations />} />
            <Route path="/healthcare/admin/icd10" element={<Icd10Codes />} />
            <Route path="/healthcare/supportive-care/iadl-reference" element={<IadlTaskReference />} />
            <Route path="/healthcare/onboarding" element={<Navigate to="/healthcare/admin/initial-setup" replace />} />
            <Route path="/healthcare/google-calendar" element={<GoogleCalendar />} />
            <Route path="/healthcare/google-drive" element={<GoogleDrive />} />
            <Route path="/healthcare/notifications" element={<NotificationEngine />} />
            <Route path="/healthcare/denial-management" element={<DenialManagement />} />
            <Route path="/healthcare/batch-operations" element={<BatchOperations />} />
            <Route path="/healthcare/dashboard-customizer" element={<DashboardCustomizer />} />
            <Route path="/healthcare/activity-feed" element={<ActivityFeed />} />
            <Route path="/healthcare/client-surveys" element={<ClientSurveys />} />
            <Route path="/healthcare/e-signatures" element={<ESignatures />} />
            <Route path="/healthcare/pa-renewal" element={<PARenewal />} />
            <Route path="/healthcare/predictive" element={<PredictiveAnalytics />} />
            <Route path="/healthcare/audit-readiness" element={<AuditReadiness />} />
            <Route path="/healthcare/clinical-compliance" element={<ClinicalCompliance />} />
            <Route path="/healthcare/grievances" element={<Grievances />} />
            <Route path="/healthcare/surveyor-packet" element={<SurveyorPacket />} />
            <Route path="/healthcare/daily-digest" element={<DailyDigest />} />
            <Route path="/healthcare/travel-log" element={<TravelLog />} />
            <Route path="/healthcare/staff-credentials" element={<StaffCredentialDashboard />} />
          </Route>
          
          {/* Redirect root based on domain */}
          <Route path="/" element={<HomeRedirect fallback={defaultRoute} />} />
          {/* Witty 404 catch-all */}
          <Route path="*" element={<ErrorPage code={404} />} />
        </Routes>
        </Suspense>
        <WalkthroughHelpButton />
        <FloatingChats />
        <HelpWidget />
        </ConfirmProvider>
      </WalkthroughProvider>
      </ErrorBoundary>
      </BrowserRouter>
      </UnsavedChangesProvider>
      </LocationProvider>
      {/* Setup Wizard — auto-opens for admins on first login when
          any required integration is still on placeholder creds. See
          components/setup/SetupWizard.jsx for the auto-open logic. */}
      <SetupWizardAutoOpener />
      <Toaster 
        position="top-right"
        richColors
        closeButton
        toastOptions={{
          // Keep the shell white/slate but let `richColors` paint the
          // left-edge accent green (success), red (error), amber (warn)
          // so users can see at a glance that a send actually landed.
          style: {
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            color: '#0f172a',
          },
        }}
      />
      </CountsProvider>
    </AuthProvider>
  );
}

export default App;
